// The ONE definition of which clock answers which question.
//
// There were four, and they disagreed. panels.ts and objects.ts rendered
// `validFrom` (the moment we WROTE the row); situations.ts grouped and sorted by
// `attrs.postedAt`; select.ts used a third preference order. Since validFrom is
// always later than postedAt, a situation card's header genuinely read OLDER
// than every child inside it. And because a merge writes a new SCD2 version,
// `valid_from` jumps to now on every corroborating report — so an event that
// kept being reported was permanently "just now", never aged out of the fade
// window, and re-sorted to the top under the reader.
//
// Three questions, three clocks, and they must not be mixed:
//
//   whenOf(e)        when the thing HAPPENED       -> row time, fade, dot colour
//   lastReportOf(e)  when we last HEARD about it   -> feed sort, pulse, ↻ chip
//   e.validFrom      when our ROW changed          -> activity log + version
//                                                     history, and nothing else
//
// That last one is the discriminating test. `panels.ts` activityRow and
// `select.ts`'s version history SHOULD read validFrom: they answer "what changed
// in our database", which is exactly what valid_from means. If a change makes
// the activity log show occurrence times, this split has been misunderstood.
//
// STAGE 2 SEAM: whenOf() is the only function that changes when the
// happened_at / happened_span / happened_basis columns land. No display site
// moves. That is the whole reason this module exists as a layer rather than as
// a search-and-replace.

import type { EntityDto } from './api';

/** Where an occurrence time came from. Ordered weakest-last on purpose: a
 *  'row' basis means we never learned a real world time and fell back to our
 *  own write log, which is worth showing differently. */
export type WhenBasis = 'stated' | 'relative' | 'post' | 'imagery' | 'manual'
  | 'rule' | 'row';

/** The bases the SERVER may send (031's CHECK constraint). 'row' is ours alone —
 *  it means the row carried no world clock and we fell back to valid_from. */
const BASES = new Set<string>(
  ['stated', 'relative', 'post', 'imagery', 'manual', 'rule']);

export interface When {
  /** Epoch ms — the best estimate of when it happened. */
  at: number;
  /** How WIDE that estimate is, in ms. 0 means "treat as exact". */
  spanMs: number;
  basis: WhenBasis;
}

// --- timing configuration (moved here from objects.ts) ------------------------
// Admin sets the defaults (public_config); each user can override fadeHours for
// their own browser. setLiveTiming patches whichever fields changed.
//
// This block lives HERE and not in objects.ts because when.ts is the module the
// display sites import; leaving it there and importing across made
// objects.ts <-> when.ts a cycle, and left two plausible homes for "which clock".
const liveTiming = {
  fadeHours: 12,       // fade out & drop past this age (0 = never fade)
  colorHours: 3,       // span of the red->grey colour ramp
  freshMinutes: 10,    // how long a fresh event pulses its halo
};

export function setLiveTiming(patch: Partial<typeof liveTiming>): void {
  Object.assign(liveTiming, patch);
}
export function liveFadeHours(): number { return liveTiming.fadeHours; }
export function liveFreshMs(): number { return liveTiming.freshMinutes * 60_000; }

/** Two times are "materially different" past this, and only then does a row
 *  show both. Below it a second number says nothing and adds noise.
 *  PROVISIONAL — not measured. */
export const MATERIAL_SKEW_MS = 45 * 60_000;

/** How long an event whose occurrence window has already aged out stays visible
 *  on the strength of a FRESH report.
 *
 *  This exists because repointing the fade window from validFrom to occurrence
 *  time is not a pure win: a backlog replay (the engine resumes up to 6 h of
 *  posts after a restart) used to land stamped "now" and therefore visible, and
 *  under an occurrence-based fade those events are already outside a 1 h window
 *  the moment they arrive. Without this band, fixing the clock would silently
 *  throw away exactly the events an operator most needs after being away.
 *  PROVISIONAL — not measured. */
export const LATE_GRACE_MS = 30 * 60_000;

// --- the accessors -----------------------------------------------------------

function num(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function iso(value: unknown): number | null {
  if (typeof value !== 'string' || !value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/** When the thing this row describes HAPPENED, as best we know.
 *
 *  Stage 1 reads what already exists. `spanMs` is deliberately 0 everywhere:
 *  until the extractor is asked for a time, every event's width would be the
 *  same invented constant, which shifts the fade window uniformly while
 *  carrying no information. Stage 2 fills it in per event. */
// MEMOISED PER ENTITY OBJECT. whenOf is called from every sort comparator,
// every grouping pass and every row render — tens of thousands of calls per
// feed repaint — and each one used to re-parse the same ISO strings
// (measured: a single sort of 4,000 events cost ~40 ms in Date.parse alone).
// The memo is keyed on the DTO object (a poll replaces the object, so a new
// version is a new key) and VALIDATED against every field the answer reads, so
// an in-place edit to attrs.date or happenedAt can never serve a stale time.
interface WhenMemo {
  hA: unknown; hS: unknown; hB: unknown; vF: unknown;
  attrs: unknown; pA: unknown; dA: unknown; dT: unknown;
  when: When;
}
const WHEN_MEMO = new WeakMap<EntityDto, WhenMemo>();
const NO_ATTRS: Record<string, unknown> = {};

export function whenOf(e: EntityDto): When {
  const a = e.attrs ?? NO_ATTRS;
  const m = WHEN_MEMO.get(e);
  if (m && m.hA === e.happenedAt && m.hS === e.happenedSpanS && m.hB === e.happenedBasis
      && m.vF === e.validFrom && m.attrs === a && m.pA === a.postedAt
      && m.dA === a.date && m.dT === a.detectedAt) {
    return m.when;
  }
  const when = computeWhen(e, a);
  WHEN_MEMO.set(e, {
    hA: e.happenedAt, hS: e.happenedSpanS, hB: e.happenedBasis, vF: e.validFrom,
    attrs: a, pA: a.postedAt, dA: a.date, dT: a.detectedAt, when,
  });
  return when;
}

function computeWhen(e: EntityDto, a: Record<string, unknown>): When {
  // Migration 031's columns, when the row has them. This arm is the whole reason
  // this module was built as a layer: it landed without a single display site
  // moving.
  const happened = iso(e.happenedAt);
  if (happened !== null) {
    const span = typeof e.happenedSpanS === 'number' && e.happenedSpanS > 0
      ? e.happenedSpanS * 1000 : 0;
    const basis = BASES.has(String(e.happenedBasis)) ? e.happenedBasis as WhenBasis
      : 'post';
    return { at: happened, spanMs: span, basis };
  }
  // Pre-031 rows, and anything the backfill could not read. A Telegram post time
  // is the tightest world clock available without the extractor's help.
  const posted = iso(a.postedAt);
  if (posted !== null) return { at: posted, spanMs: 0, basis: 'post' };
  // A damage marker's reported strike date — the same question, and already the
  // one type the tile function ages by attrs rather than valid_from.
  const dated = iso(a.date);
  if (dated !== null) return { at: dated, spanMs: 0, basis: 'imagery' };
  const detected = iso(a.detectedAt);
  if (detected !== null) return { at: detected, spanMs: 0, basis: 'post' };
  // No world clock was available. This is the ONLY sanctioned place validFrom
  // stands in for one, and the basis is what makes that visible rather than
  // silently indistinguishable from a real time.
  return { at: Date.parse(e.validFrom), spanMs: 0, basis: 'row' };
}

/** When we last heard about it. `lastReportedAt` is epoch MILLISECONDS stored
 *  as a JSON number, while postedAt/detectedAt beside it are ISO strings — a
 *  live footgun in the attrs contract, so it is read defensively here. */
interface LastMemo { when: When; lR: unknown; lRA: unknown; value: number }
const LAST_MEMO = new WeakMap<EntityDto, LastMemo>();

export function lastReportOf(e: EntityDto): number {
  // The When object is the memo's identity for the occurrence half: whenOf
  // hands back the same object only while every field it read is unchanged.
  const when = whenOf(e);
  const lRA = (e.attrs ?? NO_ATTRS).lastReportedAt;
  const m = LAST_MEMO.get(e);
  if (m && m.when === when && m.lR === e.lastReportAt && m.lRA === lRA) return m.value;
  const at = when.at;
  // 031's column first; attrs.lastReportedAt is the pre-031 form.
  const column = iso(e.lastReportAt);
  let value: number;
  if (column !== null) {
    value = Math.max(at, column);
  } else {
    const last = num(lRA);
    value = last === null ? at : Math.max(at, last);
  }
  LAST_MEMO.set(e, { when, lR: e.lastReportAt, lRA, value });
  return value;
}

/** The far edge of the occurrence estimate. What the fade window measures: a
 *  wide "overnight" guess must not be dropped on its earliest possible minute. */
export function windowEndOf(e: EntityDto): number {
  const w = whenOf(e);
  return w.at + w.spanMs;
}

export type Visibility = 'normal' | 'late' | 'hidden';

/** Whether the feed and map should show this at all. See LATE_GRACE_MS. */
export function visibilityOf(e: EntityDto): Visibility {
  const fade = liveTiming.fadeHours;
  if (fade <= 0) return 'normal';
  const now = Date.now();
  if (now - windowEndOf(e) <= fade * 3_600_000) return 'normal';
  return now - lastReportOf(e) <= LATE_GRACE_MS ? 'late' : 'hidden';
}

// --- age ramps (moved here from objects.ts; now take MILLISECONDS) -----------
// The signature change from `iso: string` to `atMs: number` is the point, not
// an incidental tidy-up: `isFaded(e.validFrom)` typechecked perfectly and so
// would `isFaded(e.happenedAt!)`, so nothing stopped the next call site being
// added with the wrong clock. `npm run build` is `tsc --noEmit`, so taking a
// number turns "audit every age call site" into a compiler error list.

/** True once past the fade window. Prefer visibilityOf() for feed/map
 *  decisions — it also honours the late-report band. */
export function isFaded(atMs: number): boolean {
  return liveTiming.fadeHours > 0
    && Date.now() - atMs > liveTiming.fadeHours * 3_600_000;
}

// Age ramp shared with the live panel: pin colour encodes HOW OLD, the
// lightning-tracker way — flashing red (the fresh pulse rides the same first
// stop) → red → orange → yellow → grey, then the opacity ramp fades it toward
// gone as the window closes. The stops span a nominal 3 h and scale with the
// configured colour-change time.
export const AGE_COLORS: [number, string][] = [
  [600_000, '#ff1744'],        // <10 min — red (and pulsing while fresh)
  [3_600_000, '#ff9100'],      // <1 h — orange
  [10_800_000, '#ffd54f'],     // <3 h — yellow
];

// `nowMs` exists for PLAYBACK: the transport replays history by asking the
// same ramps "how old was this at the playhead", so the replay ages events
// with exactly the grammar the live map uses. Defaults keep every live call
// site unchanged.
export function ageColor(atMs: number, nowMs = Date.now()): string {
  const age = nowMs - atMs;
  const scale = liveTiming.colorHours / 3;   // stops are calibrated to 3 h
  for (const [i, [upto, color]] of AGE_COLORS.entries()) {
    // Red means "happening NOW", and now does not stretch: the first stop is
    // capped at its nominal 10 minutes however long the configured ramp runs
    // (a 24 h ramp otherwise held every event — dot, halo and arrival ring —
    // red for 80 minutes). A ramp SHORTER than nominal still compresses it,
    // because a compressed ramp is asking for exactly that.
    const at = i === 0 ? upto * Math.min(1, scale) : upto * scale;
    if (age < at) return color;
  }
  return '#b0bec5';
}

// Live markers stay sharp for the first third of the fade window, then dim to
// the floor by the end of it.
const FADE_FLOOR = 0.22;

export function ageOpacity(atMs: number, nowMs = Date.now()): number {
  if (liveTiming.fadeHours <= 0) return 1;
  const h = (nowMs - atMs) / 3_600_000;
  const end = liveTiming.fadeHours;
  const start = end / 3;
  if (!Number.isFinite(h) || h <= start) return 1;
  if (h >= end) return FADE_FLOOR;
  return 1 - ((h - start) / (end - start)) * (1 - FADE_FLOOR);
}

/** Feed-row dimming: the same ramp remapped to stay legible (never below 0.5). */
export function feedOpacity(atMs: number): number {
  return 0.5 + 0.5 * (ageOpacity(atMs) - FADE_FLOOR) / (1 - FADE_FLOOR);
}

// --- rendering ---------------------------------------------------------------

/** Relative age, compact. Guards the two ways the old ISO-only version lied:
 *  a FUTURE instant rendered as "1s" — indistinguishable from the freshest
 *  possible event — and an unparseable one rendered as "NaNs". */
export function relTimeMs(atMs: number): string {
  if (!Number.isFinite(atMs)) return '—';
  const s = (Date.now() - atMs) / 1000;
  // Clock skew between us and a channel is normal and small; a genuinely future
  // timestamp is data we should not dress up as fresh news.
  if (s < -90) return 'ahead';
  if (s < 90) return `${Math.max(1, Math.round(s))}s`;
  // Minutes up to the hour; past it, hours AND minutes ("1h 18m"). "78m"
  // makes the reader do arithmetic, and a bare rounded "3h" hides up to half
  // an hour of recency exactly where the feed is judged — which of two cards
  // is fresher. On the exact hour the minutes part is noise and drops.
  const totalM = Math.round(s / 60);
  if (totalM < 60) return `${totalM}m`;
  if (s < 129600) {
    const h = Math.floor(totalM / 60);
    const m = totalM % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  return `${Math.round(s / 86400)}d`;
}

const ABS_FMT = new Intl.DateTimeFormat(undefined, {
  year: 'numeric', month: 'short', day: '2-digit',
  hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
});

/** Absolute wall-clock, ALWAYS with a zone label. Nothing in the feed exposed
 *  an absolute time at all, and an unlabelled one in OSINT work is worse than
 *  none — the reader cannot tell whose clock it is. */
export function fmtAbs(atMs: number): string {
  if (!Number.isFinite(atMs)) return 'unknown';
  return ABS_FMT.format(new Date(atMs));
}

const BASIS_NOTE: Record<WhenBasis, string> = {
  stated: 'time stated in the post',
  relative: 'estimated from wording in the post',
  post: 'time the post was published',
  imagery: 'date of the imagery',
  manual: 'set by hand',
  rule: 'generated by a rule',
  row: 'no reported time — this is when we recorded it',
};

/** The `<time>` element for a feed row. When the last report is materially
 *  later than the occurrence the row is an UPDATE, and it must read as one:
 *  "↻ update 3m" leads (that is what just happened), the original age follows
 *  dimmer ("· 5h") — never the other way around, because a 5-hour-old event
 *  wearing a bare fresh-looking clock is how late detail got mistaken for a
 *  live strike. Both wall-clocks, and the basis, stay in the tooltip. */
export function timeHtml(e: EntityDto): string {
  const w = whenOf(e);
  const last = lastReportOf(e);
  const skew = last - w.at;
  const title = [
    `Happened: ${fmtAbs(w.at)} (${BASIS_NOTE[w.basis]})`,
    skew > MATERIAL_SKEW_MS ? `Last reported: ${fmtAbs(last)}` : null,
    `Recorded: ${fmtAbs(Date.parse(e.validFrom))}`,
  ].filter(Boolean).join('\n').replace(/"/g, '&quot;');
  const stale = w.basis === 'row' ? ' when-row' : '';
  // `new Date(NaN).toISOString()` throws a RangeError, and this runs inside the
  // feed's render loop — one unparseable timestamp would blank the whole pane.
  // The same shape did exactly that in situationCard.
  const machine = Number.isFinite(w.at)
    ? ` datetime="${new Date(w.at).toISOString()}"` : '';
  if (skew > MATERIAL_SKEW_MS) {
    return `<span class="when-again" title="New reporting on an older event — `
      + `not a new occurrence. Last report: ${fmtAbs(last).replace(/"/g, '&quot;')}">`
      + `↻ update ${relTimeMs(last)}</span>`
      + `<time${machine} class="when-orig${stale}" title="${title}">${
        relTimeMs(w.at)}</time>`;
  }
  return `<time${machine} title="${title}"${
    stale ? ` class="${stale.trim()}"` : ''}>${relTimeMs(w.at)}</time>`;
}
